import {PropsWithChildren, useEffect, useState} from "react";
import {InteractionRequiredAuthError} from "@azure/msal-browser";
import {EntraIdClient} from "./EntraIdClient";
import type {EntraIdApiConfiguration} from "./EntraIdConfiguration";
import {entraIdAccessTokenExpiresAtUpdated, entraIdAuthenticated, entraIdAuthFailed, entraIdReauthenticationRequired} from "../../core/actions/entraIdAuthActions";
import {useDispatch} from "react-redux";
import {registerMsalInterceptor} from "../interceptors";
import {authLoaded} from "../../core/actions/authActions";
import Loading from "../../comp/form/loading";


type EntraIdTokenHandlerProps = PropsWithChildren<{ client: EntraIdClient; apiConfiguration: EntraIdApiConfiguration }>;

const EntraIdTokenHandler = ({ client, apiConfiguration, children }: EntraIdTokenHandlerProps): JSX.Element => {
    const [ready, setReady] = useState(false);
    const dispatch = useDispatch();

    useEffect(() => {
        let disposed = false;
        let unregister: (() => void) | undefined;

        const handleTokenFailure = (error: unknown): void => {
            if (disposed) {
                return;
            }
            if (error instanceof InteractionRequiredAuthError) {
                dispatch(entraIdReauthenticationRequired());
                return;
            }
            console.error('MSAL runtime token retrieval failed:', error);
            dispatch(entraIdAuthFailed());
        };

        const getTokenAndUpdateExpiry = async (): Promise<string> => {
            const token = await client.getAccessToken();
            if (!disposed) {
                dispatch(entraIdAccessTokenExpiresAtUpdated(client.getAccessTokenExpiresAt()));
            }
            return token;
        };

        const handleToken = async (): Promise<void> => {
            try {
                const token = await client.getAccessToken();
                if (disposed) {
                    return;
                }
                if (!token) {
                    dispatch(entraIdAuthFailed());
                    return;
                }
                unregister = registerMsalInterceptor(getTokenAndUpdateExpiry, handleTokenFailure, apiConfiguration);
                dispatch(authLoaded({ access_token: token }));
                dispatch(entraIdAuthenticated());
                dispatch(entraIdAccessTokenExpiresAtUpdated(client.getAccessTokenExpiresAt()));
                setReady(true);
            } catch (error) {
                if (disposed) {
                    return;
                }
                if (error instanceof InteractionRequiredAuthError) {
                    dispatch(entraIdReauthenticationRequired());
                    return;
                }
                console.error('MSAL token retrieval failed:', error);
                dispatch(entraIdAuthFailed());
            }
        };

        handleToken();

        return () => {
            disposed = true;
            unregister?.();
        };
    }, [client, apiConfiguration, dispatch]);

    if (!ready) {
        return <Loading text={undefined} />;
    }

    return <>{children}</>;
};

export default EntraIdTokenHandler;

